/*======================================================================================================================
  * Bluebell: src/app/state/postpro/postpro.effects.ts
  * Last Edited: 08/08/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { inject, Injectable } from '@angular/core';
import { convertToReference } from '@app/components/postpro-viewer/postpro-viewer.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import {
  selectResults,
  setSelectedResults,
  deselectResults,
  selectAllResults,
} from '@state/results/results.actions';
import { selectSelectedResultsIds } from '@state/results/results.selectors';
import { ApiService } from '@state/api.service';
import { selectAccessToken } from '@state/authentication/authentication.selectors';
import {
  listPostproAvailableVariables,
  listPostproAvailableVariablesSuccess,
  listPostproAvailableVariablesFailure,
  clearPostproAvailableVariables,
  selectPostproVariable,
  loadPostproReferences,
  reselectLoadedReferences,
  loadPostproReferencesSuccess,
  loadPostproReferencesFailure,
  loadLICReferences,
  loadLICReferencesSuccess,
  loadLICReferencesFailure,
} from '@state/postpro/postpro.actions';
import { PostproAvailableVariables, Postpro } from '@state/postpro/postpro.interfaces';
import {
  selectLoadedPostproVariables,
} from '@state/postpro/postpro.selectors';
import { filter, map, switchMap } from 'rxjs/operators';


@Injectable()
export class PostproEffects {
  actions$ = inject(Actions);
  api = inject(ApiService);
  store = inject(Store);
  handler = inject(EffectHandlerService);

  /**
   * Variables
   */

  // checkForTypeChange = createEffect(() => this.actions$.pipe(
  //   ofType(selectPostproVariable),
  //   concatLatestFrom(() => this.store.select(selectSelectedPostproType)),
  //   filter(([{ postproType }, selectedType]) => selectedType && postproType !== selectedType),
  //   map(([{ postproType }, _]) => changeSelectedPostproType({ postproType })),
  // ));

  // When a variable is selected
  selectVariable = createEffect(() => this.actions$.pipe(
    ofType(selectPostproVariable),
    filter(({ postproType }) => ['plot', 'check', 'movie'].includes(postproType)),
    concatLatestFrom(() => [
      this.store.select(selectSelectedResultsIds),
      this.store.select(selectLoadedPostproVariables)
    ]),
    map(([{ variable, postproType, camera, casesChanged }, cases, loadedVariables]) => {
      if (loadedVariables.includes(variable) && !casesChanged) {
        return reselectLoadedReferences({ variable, camera });
      } else {
        return loadPostproReferences({ variable, postproType, cases, camera });
      }
    })
  ));

  /**
   * References
   */

  loadReferences$ = createEffect(() => this.actions$.pipe(
    ofType(loadPostproReferences),
    switchMap(({ variable, postproType, cases, camera }) => this.api.listPostproReferences(variable, postproType, cases, camera).pipe(
      this.handler.effectResponse({
        retry: loadPostproReferences({ variable, postproType, cases, camera }),
        success: (references: Postpro[]) => loadPostproReferencesSuccess({ references, variable, camera }),
        failure: loadPostproReferencesFailure({ variable, camera }),
        failureMessage: 'Could not load postpro references'
      })
    ))
  ));

  /**
   * Available Postpro
   */

  loadAvailableVariables$ = createEffect(() => this.actions$.pipe(
    ofType(listPostproAvailableVariables),
    switchMap(({ uuids }) => this.api.listPostproAvailableVariables(uuids).pipe(
      this.handler.effectResponse({
        retry: listPostproAvailableVariables({ uuids }),
        success: (availableVariables: PostproAvailableVariables) => listPostproAvailableVariablesSuccess({ availableVariables }),
        failure: listPostproAvailableVariablesFailure(),
        failureMessage: 'Could not load postpro available variables'
      })
    ))
  ));

  onResultsSelection$ = createEffect(() => this.actions$.pipe(
    ofType(
      selectResults,
      selectAllResults,
      setSelectedResults,
      deselectResults,
    ),
    concatLatestFrom(() => this.store.select(selectSelectedResultsIds)),
    map(([, uuids]: [unknown, string[]]) => uuids?.length
      ? listPostproAvailableVariables({ uuids })
      : clearPostproAvailableVariables()
    )
  ));

  loadLICPostpro$ = createEffect(() => this.actions$.pipe(
    ofType(loadLICReferences),
    concatLatestFrom(() => [
      this.store.select(selectSelectedResultsIds),
      this.store.select(selectAccessToken),
    ]),
    filter((payload): payload is [ReturnType<typeof loadLICReferences>, string[], string] => !!payload[2]),
    switchMap(([{ postproType, camera }, cases, token]) => this.api.listPostproReferences('LIC', postproType, cases, camera).pipe(
      this.handler.effectResponse({
        retry: loadLICReferences({ postproType, camera }),
        success: (licReferences: Postpro[]) => loadLICReferencesSuccess({
          licReferences: licReferences.map(reference => convertToReference(reference, token))
        }),
        failure: loadLICReferencesFailure(),
        failureMessage: 'Could not load LIC postpro references'
      })
    ))
  ));

}
